/**
 * @fileoverview The plant card's weather as one caption line (B136, design.md rev 12): the server's icon, a bold lead that names
 * the weather and the days ("Cold nights Mon-Thu"), the closed sentence that is the step ("Harvest what you can, or cover it
 * those nights."), parted from the lead by a space and flowing with it as one inline paragraph, and "... more", which opens the week's story under it (two short paragraphs) and "... less", which shuts it.
 * It replaces the week bar and the verdict chips on the card, and every card that has a week has one.
 *
 * The caption is the server's from the first word to the last (services/plants/plantWeekCaption, in the brief's language,
 * week.caption and week.icon); this draws it and works out no weather. The lead and the icon take the week's colour from the
 * brief's token (cool, warm, wet, good), as the badges do. Opened, the story is followed by the week's grid and what it means
 * (TemperatureForecast), which used to sit behind the card's week fold. Whether the story is open is the card's one-tray-at-a-time state, so
 * the card holds it and gives it here.
 *
 * Where: the plant card (PlantCard), under the photo and above the Care strip.
 * API: none of its own; the caption is the plants brief's (usePlantsBrief: week.caption, week.icon, week.token).
 * Words: none of its own; "more" and "less" are the brief's.
 *
 * @module WeekCaption
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { WeekIcon } from '@/shared/components/WeekIcon';
import { TOKEN_TEXT } from '@/shared/theme/verdictTokens';
import type { BriefWeek } from '@/shared/api/plantsBrief';
import { TemperatureForecast } from '../TemperatureForecast';

/** What the caption is given. */
interface WeekCaptionProps {
  /** The plant (plant_obj_id): the opened story holds its week grid. */
  plantObjId: number;
  /** The plant's week, as the brief sends it. */
  week: BriefWeek;
  /** Whether the story is open (the card's one open tray). */
  open: boolean;
  /** Opens or shuts the story. */
  onToggle: () => void;
}

/**
 * The caption: icon, lead, closed sentence, and "... more" / "... less" with the story between.
 *
 * @param props - WeekCaptionProps.
 */
export const WeekCaption: React.FC<WeekCaptionProps> = ({ plantObjId, week, open, onToggle }) => {
  const { caption } = week;
  const toggle = (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="whitespace-nowrap rounded-sm font-semibold text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {open ? caption.less : caption.more}
    </button>
  );
  return (
    <div className="px-4 pb-1.5 pt-2.5 text-sm leading-[1.45]">
      {/* One inline paragraph on one baseline: the icon sits in the lead's text line, a space (not a margin) parts the lead from the sentence. */}
      <span className={cn('font-bold', week.token ? TOKEN_TEXT[week.token] : 'text-foreground')}>
        <WeekIcon name={week.icon} className="mr-1.5 inline-block h-[1.125rem] w-[1.125rem] align-[-0.2em]" strokeWidth={2.2} aria-hidden="true" />
        {caption.lead}
      </span>
      {' '}
      {caption.sentence}{' '}
      {!open && toggle}
      {open && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {caption.story.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          {/* The week's grid, what it means and the week's items (it left the card's face with the fold; nothing is lost). */}
          <TemperatureForecast plantObjId={plantObjId} />
          <p>{toggle}</p>
        </div>
      )}
    </div>
  );
};
